// Reusable tier badge for customers. Per spec/14 "Tier semantics" + "Tier badge":
//   vip       → gold star
//   good      → green checkmark
//   slow_pay  → amber dollar-with-clock
//   bad       → red/orange exclamation
//   new       → grey sprout
//
// Used by customer cards, the customer banner, quote rows — anywhere a
// CustomerTier needs to render. Three sizes:
//   sm — icon only, tight pill
//   md — icon + short label (default)
//   lg — icon + label, hover tooltip with longer description
//
// Tailwind classes only; palette is project-standard (orange/navy/green/amber/steel).
// Pure presentational — no Firebase reads, no router deps.
import type { CustomerTier } from '@/shared/crmTypes';

export interface TierBadgeProps {
  tier: CustomerTier;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

interface TierVisual {
  icon: string;
  label: string;
  description: string;
  /** Tailwind class string for background + text. */
  classes: string;
}

const TIER_VISUALS: Record<CustomerTier, TierVisual> = {
  vip: {
    icon: '★',
    label: 'VIP',
    description: 'Top-tier customer — highest service level, priority handling',
    classes: 'bg-amber/15 text-amber border border-amber/40',
  },
  good: {
    icon: '✓',
    label: 'Good',
    description: 'Reliable customer — standard pricing, warm follow-up tone',
    classes: 'bg-green/15 text-green border border-green/40',
  },
  slow_pay: {
    icon: '$⏱',
    label: 'Slow Pay',
    description: 'Pays but late — carrying cost factored into pricing',
    classes: 'bg-amber/20 text-amber border border-amber/50',
  },
  bad: {
    icon: '!',
    label: 'Bad',
    description: 'Quote requires Justin approval before sending; AR no credit',
    classes: 'bg-orange/15 text-orange border border-orange/40',
  },
  new: {
    icon: '🌱',
    label: 'New',
    description: 'New customer — no history yet, awaiting first job',
    classes: 'bg-steel/15 text-steel border border-steel/40',
  },
};

const SIZE_CLASSES: Record<NonNullable<TierBadgeProps['size']>, string> = {
  sm: 'text-[10px] px-1.5 py-0.5 gap-0.5',
  md: 'text-xs px-2 py-0.5 gap-1',
  lg: 'text-sm px-2.5 py-1 gap-1.5',
};

// Defensive fallback for customers whose tier field is missing / typo'd /
// imported from a source that doesn't speak the v2 enum. Renders as a muted
// "?" pill so the list page survives one bad row instead of unmounting the
// whole tree (battle-tested: a single undefined tier was crashing /customers).
const UNKNOWN_VISUAL: TierVisual = {
  icon: '?',
  label: 'Unknown',
  description: 'Customer tier not set — fix on the customer detail page',
  classes: 'bg-bg text-text-mute border border-line-dark',
};

export function TierBadge({ tier, size = 'md', showLabel = true }: TierBadgeProps) {
  const visual = TIER_VISUALS[tier] ?? UNKNOWN_VISUAL;
  const sizeCls = SIZE_CLASSES[size];

  // sm always icon-only regardless of showLabel — keeps the contract simple.
  const renderLabel = showLabel && size !== 'sm';

  return (
    <span
      className={`inline-flex items-center rounded-full font-semibold whitespace-nowrap ${sizeCls} ${visual.classes}`}
      title={size === 'lg' ? visual.description : `${visual.label} — ${visual.description}`}
      aria-label={`${visual.label} tier`}
    >
      <span aria-hidden>{visual.icon}</span>
      {renderLabel && <span>{visual.label}</span>}
    </span>
  );
}
